import { Component, type ReactNode } from 'react';
import leadlancerLogo from '@/assets/leadlancer-logo.png';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppErrorBoundary]', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="dark">
          <div className="min-h-screen bg-background flex items-center justify-center px-6">
            <div className="max-w-md w-full text-center space-y-6">
              <img
                src={leadlancerLogo}
                alt="LeadLancer"
                className="h-10 mx-auto opacity-80"
              />
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-foreground">
                  Something went wrong
                </h1>
                <p className="text-muted-foreground text-sm">
                  An unexpected error occurred. Please reload the page to try again.
                </p>
              </div>
              {import.meta.env.DEV && this.state.error && (
                <pre className="text-xs text-destructive bg-destructive/10 rounded-lg p-4 text-left overflow-auto max-h-40">
                  {this.state.error.message}
                </pre>
              )}
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
